// Habillage des composants de score : anneau, panneau, comparateur.
//
// Même raison d'être et mêmes règles que 'primitivesCss' — couleurs toujours
// issues de '--s-*', géométrie en littéraux — mais dans une feuille SÉPARÉE,
// pour une raison concrète : le content script de l'extension injecte dans un
// shadow-root où il ne veut PAS de tout le socle de boutons (il a le sien,
// écrit en Tailwind), alors qu'il veut l'anneau. Les deux apps prennent donc
// ce qui les concerne.
//
// DÉTAIL QUI PIÈGE : la feuille est un littéral de gabarit. Un backtick dans
// un commentaire CSS ferme la chaîne et casse le build sur une erreur qui
// désigne une ligne sans rapport. Citer les sélecteurs entre 'apostrophes'.

import { mouvementCss } from './mouvement'

export const scoreCss = mouvementCss + `
/* === Anneau de score ==============================================
   La forme ronde du widget, seule représentation du score dans tout le
   produit. La taille arrive en '--ring-size' (inline) pour que le chiffre
   central suive le diamètre sans une classe par taille. */
/* Enveloppe neutre : sans mot de ton elle se comporte comme si '.score-ring'
   était seule (même alignement en ligne), avec le mot elle empile le mot
   sous l'anneau plutôt que de le laisser déborder du carré de l'anneau. */
.score-ring-wrap { display: inline-flex; flex-direction: column; align-items: center; gap: 4px; }
.score-ring-tone {
  font-size: var(--s-text-xs);
  font-weight: 700;
  line-height: 1.2;
}
.score-ring-tone.good { color: var(--s-good-strong); }
.score-ring-tone.mid  { color: var(--s-warn); }
.score-ring-tone.low  { color: var(--s-danger); }
.score-ring {
  position: relative;
  width: var(--ring-size, 88px);
  height: var(--ring-size, 88px);
  flex-shrink: 0;
}
/* Départ à midi plutôt qu'à 3 h : un arc qui commence en haut se lit comme une
   jauge, pas comme un camembert. */
.score-ring svg { transform: rotate(-90deg); display: block; }
.score-ring .ring-track { stroke: var(--s-track); }
.score-ring .ring-cur, .score-ring .ring-head {
  transition: stroke-dasharray .6s cubic-bezier(.4,0,.2,1);
}
/* L'anneau qui monte (issue #183) : l'arc part de zéro vers sa valeur (une
   animation sans 'to' va jusqu'au tracé posé sur l'élément), la marge se
   dessine ensuite depuis le bout de l'arc, et le mot de ton se pose. */
.score-ring.anime .ring-cur { animation: ringMonte var(--m-chiffre, 900ms) var(--m-courbe, cubic-bezier(.2,.8,.2,1)) both; }
.score-ring.anime .ring-head { animation: ringMarge var(--m-arrivee, 320ms) var(--m-courbe, cubic-bezier(.2,.8,.2,1)) calc(var(--m-chiffre, 900ms) - 150ms) both; }
.score-ring-wrap.anime .score-ring-tone { animation: ringMot var(--m-arrivee, 320ms) ease-out calc(var(--m-chiffre, 900ms) - 250ms) both; }
@keyframes ringMonte { from { stroke-dasharray: 0 400; } }
@keyframes ringMarge { from { stroke-dasharray: var(--arc-score); opacity: 0; } }
@keyframes ringMot { from { opacity: 0; transform: translateY(4px); } }
@media (prefers-reduced-motion: reduce) {
  .score-ring.anime .ring-cur, .score-ring.anime .ring-head, .score-ring-wrap.anime .score-ring-tone { animation: none; }
}
.score-ring .ring-cur.good { stroke: var(--s-good); }
.score-ring .ring-cur.mid  { stroke: var(--s-warn-fill); }
.score-ring .ring-cur.low  { stroke: var(--s-danger); }
/* L'arc du POTENTIEL, en retrait : il dit ce qui est atteignable, pas acquis. */
.score-ring .ring-head.good { stroke: var(--s-good-soft); }
.score-ring .ring-head.mid  { stroke: var(--s-warn-soft); }
.score-ring .ring-head.low  { stroke: var(--s-danger-soft); }
.score-ring-center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1;
}
.score-ring-num {
  font-size: calc(var(--ring-size, 88px) * .3);
  font-weight: 800;
  line-height: 1;
  letter-spacing: -.02em;
  font-variant-numeric: tabular-nums;
}
.score-ring-num.good { color: var(--s-good-strong); }
.score-ring-num.mid  { color: var(--s-warn); }
.score-ring-num.low  { color: var(--s-danger); }
.score-ring-max {
  font-size: calc(var(--ring-size, 88px) * .125);
  font-weight: 600;
  color: var(--s-ink-3);
  margin-top: 2px;
}

/* === Panneau de score =============================================
   Volontairement sans verdict : voir l'en-tête de ScorePanel.tsx. */
.scorepanel { display: flex; flex-direction: column; gap: 16px; }

.scorehead {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 16px 20px;
  border-radius: 16px;
  background: var(--s-surface);
  border: 1px solid var(--s-line);
}
/* Un liseré teinté du palier, pas un fond teinté : le fond coloré derrière un
   anneau déjà coloré donnait deux aplats concurrents pour la même information. */
.scorehead.good { border-color: var(--s-good-soft); }
.scorehead.mid  { border-color: var(--s-warn-soft); }
.scorehead.low  { border-color: var(--s-line-strong); }
.scoremeta { min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.scoresubject { font-weight: 800; font-size: var(--s-text-lg); line-height: 1.2; }
.scorerole { font-size: var(--s-text-sm); color: var(--s-ink-2); }
/* Le potentiel est une PILULE et non une ligne de texte : c'est le second
   chiffre qui compte, et il doit se retrouver d'un coup d'oeil. */
.scorepotential {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  align-self: flex-start;
  margin-top: 3px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: var(--s-text-xs);
  font-weight: 700;
  background: var(--s-accent-tint);
  color: var(--s-accent);
  border: 1px solid var(--s-accent-border);
}
.scorehead-aside { margin-left: auto; display: flex; align-items: center; gap: 8px; }

/* === Mode compact (44 px, la taille du widget) =====================
   Demandé le 25 août 2026 : le panneau plein dominait un écran où il
   n'est plus la seule chose à montrer, offre et dossier étant réunis.
   Un bandeau qu'on lit d'un regard, le détail se demande. */
.scorepanel.compact { gap: 0; }
.scorepanel.compact .scorehead { padding: 10px 14px; gap: 12px; border-radius: 12px; }
.scoretone-inline { font-size: var(--s-text-xs); font-weight: 700; }
.scoretone-inline.good { color: var(--s-good-strong); }
.scoretone-inline.mid  { color: var(--s-warn); }
.scoretone-inline.low  { color: var(--s-ink-2); }
.detail-toggle {
  border: 1px solid var(--s-line); background: var(--s-surface); color: var(--s-ink-2);
  border-radius: 8px; padding: 5px 11px; font-size: var(--s-text-xs); font-weight: 700;
  display: inline-flex; align-items: center; gap: 5px;
}
.detail-toggle:hover { background: var(--s-surface-2); }
.detail-toggle[aria-expanded="true"] { border-color: var(--s-accent); color: var(--s-accent); }
.scorepanel.compact .dims,
.scorepanel.compact .gaps,
.scorepanel.compact .gapbox.block { margin-top: 16px; }

/* === Micro-retour 👍/👎 ============================================= */
.scorefeedback { display: flex; gap: 4px; }
.fb-btn {
  width: 30px; height: 30px; border-radius: 8px; border: 1px solid var(--s-line);
  background: var(--s-surface); display: grid; place-items: center; font-size: 14px; line-height: 1;
  transition: transform .1s cubic-bezier(.4,0,.2,1), border-color .15s cubic-bezier(.4,0,.2,1);
}
.fb-btn:hover { transform: scale(1.08); border-color: var(--s-line-strong); }
.fb-btn[aria-pressed="true"] { border-color: var(--s-accent); background: var(--s-accent-tint); }
.score-avis-lien { margin-top: 10px; font-size: var(--s-text-xs); color: var(--s-ink-3); }
.score-avis-lien .linkish { background: none; border: 0; padding: 0; font: inherit; color: var(--s-ink-2); text-decoration: underline; text-underline-offset: 2px; cursor: pointer; }
.score-avis-lien .linkish:hover { color: var(--s-accent); }

/* === Dimensions =================================================== */
.dims { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 24px; }
.dim .dh { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; font-size: var(--s-text-sm); margin-bottom: 6px; }
.dim .dh b { font-weight: 700; }
.dim .dh .dv { font-weight: 800; font-variant-numeric: tabular-nums; color: var(--s-ink-2); }
.dim .dr { font-size: var(--s-text-xs); color: var(--s-ink-3); margin-top: 5px; line-height: 1.45; }
.track { height: 8px; border-radius: 999px; background: var(--s-track); overflow: hidden; }
.track .fill { height: 100%; border-radius: 999px; display: block; transition: width .5s cubic-bezier(.4,0,.2,1); }
.track .fill.good { background: var(--s-good); }
.track .fill.mid  { background: var(--s-warn-fill); }
.track .fill.low  { background: var(--s-danger); }

/* === Atouts / écarts / couperets ================================== */
.gaps { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
/* Exigences citées (issue #162) : repliées, une ligne par exigence. La
   couleur dit l'état, comme partout ailleurs, et rien d'autre. */
.reqs { margin-top: 12px; border: 1px solid var(--s-line); border-radius: 14px; padding: 10px 16px; background: var(--s-surface-2); }
.reqs summary { cursor: pointer; font-size: var(--s-text-xs); font-weight: 800; text-transform: uppercase; letter-spacing: .04em; color: var(--s-ink-2); }
.reqs ul, .reqs-widget ul { margin: 10px 0 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 8px; }
.req { display: flex; gap: 8px; font-size: var(--s-text-sm, 13px); line-height: 1.4; color: var(--s-ink); }
.req q { font-style: italic; }
.req-mot { display: inline-block; margin-right: 6px; font-size: var(--s-text-xs); font-weight: 800; text-transform: uppercase; letter-spacing: .03em; }
.req.met .req-mot { color: var(--s-good-strong); }
.req.partial .req-mot { color: var(--s-warn); }
.req.missing .req-mot { color: var(--s-danger); }
.req-ev { display: block; font-size: var(--s-text-xs); color: var(--s-ink-2); margin-top: 2px; }
/* Ce que l'offre demande se coche une ligne à la fois (#187), à la première
   apparition de la liste ; refermer puis rouvrir ne rejoue pas (les lignes
   restent montées). */
.reqs.anime .req { animation: reqLigne var(--m-arrivee, 320ms) ease-out both; animation-delay: calc(150ms + var(--k, 0) * var(--m-cascade, 90ms) * 3); }
.reqs.anime .req-mark { display: inline-block; animation: reqCoche var(--m-arrivee, 320ms) var(--m-courbe, cubic-bezier(.2,.8,.2,1)) both; animation-delay: calc(250ms + var(--k, 0) * var(--m-cascade, 90ms) * 3); }
@keyframes reqLigne { from { opacity: 0; transform: translateX(-6px); } }
@keyframes reqCoche { 0% { transform: scale(0); } 60% { transform: scale(1.35); } }
@media (prefers-reduced-motion: reduce) { .reqs.anime .req, .reqs.anime .req-mark { animation: none; } }
.req-mark { flex-shrink: 0; width: 14px; font-weight: 800; }
.req.met .req-mark { color: var(--s-good-strong); }
.req.partial .req-mark { color: var(--s-warn); }
.req.missing .req-mark { color: var(--s-danger); }
.req-wait { margin: 8px 0 0; font-size: var(--s-text-xs); color: var(--s-ink-2); }
.gapbox { border: 1px solid var(--s-line); border-radius: 14px; padding: 12px 16px; background: var(--s-surface-2); }
.gapbox .gh {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: var(--s-text-xs);
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .04em;
  margin-bottom: 8px;
}
.gapbox.plus .gh  { color: var(--s-good-strong); }
.gapbox.minus .gh { color: var(--s-warn); }
.gapbox.block .gh { color: var(--s-danger); }
.gapbox.block { border-color: var(--s-danger-soft); background: var(--s-danger-tint); }
.gapbox ul { margin: 0; padding: 0; list-style: none; display: flex; flex-wrap: wrap; gap: 5px; }
/* Des étiquettes plutôt qu'une liste à puces : ce sont des mots-clés, pas des
   phrases, et empilés ils faisaient une colonne de deux caractères de large. */
.gapbox li {
  font-size: var(--s-text-xs);
  font-weight: 600;
  color: var(--s-ink-2);
  background: var(--s-surface);
  border: 1px solid var(--s-line);
  border-radius: 999px;
  padding: 3px 10px;
}
.gapbox.block li { border-color: var(--s-danger-soft); color: var(--s-danger); background: var(--s-surface); }

/* === Comparateur ==================================================
   Une COLONNE par profil, une LIGNE par critère : c'est le seul agencement où
   l'oeil compare verticalement sans relire les libellés. La grille est posée
   inline (le nombre de colonnes dépend de la sélection). */
.cmp {
  border: 1px solid var(--s-line);
  border-radius: 16px;
  background: var(--s-surface);
  overflow: hidden;
}
/* Le débordement horizontal est CONFINÉ ici : quatre profils sur un portable
   dépassent, et c'est le tableau qui doit défiler, jamais la page.
   Pas de 'min-width: max-content' sur la grille : une seule étiquette longue
   ('modélisation star schema (Kimball)') suffisait alors à élargir toutes les
   colonnes, et trois profils ne tenaient plus sur un écran de 1440 px. La
   largeur plancher est calculée par le composant, à partir du NOMBRE de
   colonnes, et les étiquettes reviennent à la ligne. */
.cmp-scroll { overflow-x: auto; }
.cmp-grid { display: grid; }
.cmp-rowlabel {
  display: flex;
  align-items: center;
  font-size: var(--s-text-xs);
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: var(--s-ink-3);
  padding: 12px 16px;
  position: sticky;
  left: 0;
  background: var(--s-surface);
  z-index: 1;
  border-right: 1px solid var(--s-line);
}
.cmp-cell { padding: 12px 16px; min-width: 190px; border-left: 1px solid var(--s-line); }
.cmp-row-sep > * { border-top: 1px solid var(--s-line); }
.cmp-band > * { background: var(--s-surface-2); }
.cmp-band .cmp-rowlabel { background: var(--s-surface-2); }

.cmp-cell.cmp-headcell { position: relative; }
.cmp-head { display: flex; flex-direction: column; align-items: center; gap: 8px; text-align: center; }
.cmp-name { font-weight: 800; font-size: var(--s-text-base); line-height: 1.2; }
.cmp-role { font-size: var(--s-text-xs); color: var(--s-ink-3); }
/* Au coin de la colonne, pas dans le flux : posée sous le rôle, la croix
   passait pour une puce du texte. 26 px de côté pour rester cliquable. */
.cmp-drop {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 26px;
  height: 26px;
  display: grid;
  place-items: center;
  border: 0;
  background: none;
  color: var(--s-ink-3);
  cursor: pointer;
  padding: 0;
  border-radius: 999px;
  font-size: var(--s-text-sm);
  line-height: 1;
}
.cmp-drop:hover { color: var(--s-danger); background: var(--s-danger-tint); }

/* La MEILLEURE valeur d'une ligne, marquée une seule fois par ligne. Sans ce
   repère, comparer quatre barres revient à mesurer des pixels à l'oeil. */
.cmp-val { display: flex; align-items: baseline; gap: 7px; margin-bottom: 6px; }
.cmp-num { font-weight: 800; font-variant-numeric: tabular-nums; font-size: var(--s-text-md); }
.cmp-best {
  font-size: var(--s-text-xs);
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: var(--s-accent);
  background: var(--s-accent-tint);
  border-radius: 999px;
  padding: 1px 8px;
}
.cmp-delta { font-size: var(--s-text-xs); font-weight: 700; color: var(--s-ink-3); font-variant-numeric: tabular-nums; }
.cmp-tags { display: flex; flex-wrap: wrap; gap: 5px; }
.cmp-tag {
  font-size: var(--s-text-xs);
  font-weight: 600;
  border-radius: 999px;
  padding: 3px 9px;
  border: 1px solid var(--s-line);
  background: var(--s-surface-2);
  color: var(--s-ink-2);
}
.cmp-tag.uniq { border-color: var(--s-good-soft); background: var(--s-good-tint); color: var(--s-good-strong); }
.cmp-tag.block { border-color: var(--s-danger-soft); background: var(--s-danger-tint); color: var(--s-danger); }
.cmp-none { font-size: var(--s-text-xs); color: var(--s-ink-3); }
.cmp-foot { padding: 12px 16px; }

@media (max-width: 720px) {
  .dims, .gaps { grid-template-columns: 1fr; }
  .scorehead { flex-wrap: wrap; }
  .scorehead-aside { margin-left: 0; width: 100%; }
}
`
